import { useMemo, useState } from "react";

import { isChartDataEmpty } from "../hooks/utils";

import DeploymentUsageChartContent from "./DeploymentUsageChartContent";
import DeploymentUsageChartControls from "./DeploymentUsageChartControls";
import TotalTokensBadge from "./TotalTokensBadge";
import { useDeploymentUsageChartData } from "./hooks/useDeploymentUsageChartData";
import { useDeploymentUsageChartOptions } from "./hooks/useDeploymentUsageChartOptions";
import { DeploymentChartType, DeploymentTypeFilter, DeploymentUsageChartProps } from "./types";

export const DeploymentUsageChart = ({ dateRange }: DeploymentUsageChartProps) => {
  const [chartType, setChartType] = useState<DeploymentChartType>("periodic");
  const [deploymentTypeFilter, setDeploymentTypeFilter] = useState<DeploymentTypeFilter>("all");

  const { chartData, deploymentSeries, totalTokens, totalSessions, isError, isChartLoading } =
    useDeploymentUsageChartData({
      chartType,
      dateRange,
      deploymentTypeFilter,
    });
  const chartOptions = useDeploymentUsageChartOptions({
    chartData,
    deploymentSeries,
  });
  const isEmpty = useMemo(() => isChartDataEmpty(chartData), [chartData]);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md">
      <div className="mb-4 flex items-start justify-between gap-3 p-6 pb-0">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h4 className="text-lg font-semibold leading-6 text-gray-900">Deployment Usage</h4>
          <p className="text-sm leading-5 text-gray-600">
            Daily token usage stacked by deployment for the selected date range
          </p>
        </div>
        <TotalTokensBadge
          isLoading={isChartLoading}
          totalTokens={totalTokens}
          totalSessions={totalSessions}
        />
      </div>

      <DeploymentUsageChartContent
        chartOptions={chartOptions}
        isChartLoading={isChartLoading}
        isEmpty={isEmpty}
        isError={isError}
      />
      <div className="min-h-0 flex-1" aria-hidden="true" />
      <DeploymentUsageChartControls
        chartType={chartType}
        deploymentTypeFilter={deploymentTypeFilter}
        onChartTypeChange={setChartType}
        onDeploymentTypeFilterChange={setDeploymentTypeFilter}
      />
    </div>
  );
};
